/**
 * MARKETPLACE CREATION CARD
 * 
 * Displays a creation (press-on set, jewelry, etc.) in the marketplace
 * Modeled after MarketplaceItemCard for consistent UX
 * 
 * Features:
 * - Photo display with watch (star) toggle
 * - Category badge (Press-Ons/Jewelry/Other)
 * - Status badge (Available/Pending/Sold)
 * - Price display
 * - Time listed indicator
 * - Seller info with favorite heart toggle
 * - "Message Seller" button
 * - Login prompt modal
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  doc, getDoc, collection, getDocs, addDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  MessageSquare, ExternalLink, Heart, Loader2,
  Clock, Tag, Sparkles, Star
} from 'lucide-react';
import { AppIcons } from '../../constants/icons';
import InquiryModal from '../../messaging/components/InquiryModal';
import type { Creation } from '../../types/creationTypes';
import type { AttachedItem } from '../../messaging/types';

interface MarketplaceCreation extends Creation {
  sellerId: string;
  sellerUsername?: string;
  marketplaceListedAt?: any;
}

interface MarketplaceCreationCardProps {
  creation: MarketplaceCreation;
  currentUserId: string | null;
  onClick?: () => void;
  isWatching?: boolean;
  onWatchChange?: (isWatching: boolean) => void;
}

const MarketplaceCreationCard: React.FC<MarketplaceCreationCardProps> = ({ 
  creation, 
  currentUserId,
  onClick,
  isWatching: externalIsWatching,
  onWatchChange
}) => {
  // Seller state
  const [sellerUsername, setSellerUsername] = useState<string | null>(null);
  const [sellerAvatar, setSellerAvatar] = useState<string | null>(null);
  
  // Modal state
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Watching state (star icon) - use external prop if provided
  const [internalIsWatching, setInternalIsWatching] = useState(false);
  const isWatching = externalIsWatching !== undefined ? externalIsWatching : internalIsWatching;
  const [watchingLoading, setWatchingLoading] = useState(false);
  
  // Favorite seller state (heart icon)
  const [isSellerFavorited, setIsSellerFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Get primary photo
  const primaryPhoto = creation.photos?.find(p => p.isDefault) || creation.photos?.[0];
  const photoUrl = primaryPhoto?.url;

  // Fetch seller info
  useEffect(() => {
    const fetchSeller = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', creation.sellerId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setSellerUsername(data.username || data.profile?.username || null);
          setSellerAvatar(data.avatarUrl || data.profile?.avatarUrl || null);
        }
      } catch (error) {
        console.error('Error fetching seller:', error);
      }
    };
    
    if (creation.sellerId) {
      fetchSeller();
    }
  }, [creation.sellerId]);

  // Check if creation is being watched (only if not controlled externally)
  useEffect(() => {
    // Skip if externally controlled
    if (externalIsWatching !== undefined) return;
    
    const checkWatching = async () => {
      if (!currentUserId) return;
      
      try {
        const watchingRef = collection(db, 'users', currentUserId, 'watchingCreations');
        const q = await getDocs(watchingRef);
        const isOnList = q.docs.some(doc => 
          doc.data().creationId === creation.id && doc.data().creatorId === creation.sellerId
        );
        setInternalIsWatching(isOnList);
      } catch (err) {
        console.error('Error checking watching status:', err);
      }
    };

    checkWatching();
  }, [currentUserId, creation.id, creation.sellerId, externalIsWatching]);

  // Check if seller is favorited
  useEffect(() => {
    const checkFavorite = async () => {
      if (!currentUserId || !creation.sellerId) return;
      
      try {
        const favoritesRef = collection(db, 'users', currentUserId, 'favoriteSellers');
        const q = await getDocs(favoritesRef);
        const isFavorited = q.docs.some(doc => doc.data().sellerId === creation.sellerId);
        setIsSellerFavorited(isFavorited);
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };

    checkFavorite();
  }, [currentUserId, creation.sellerId]);

  // Toggle watching
  const handleWatchToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    setWatchingLoading(true);
    try {
      const watchingRef = collection(db, 'users', currentUserId, 'watchingCreations');
      
      if (isWatching) {
        // Remove from watching
        const q = await getDocs(watchingRef);
        const docToDelete = q.docs.find(d => 
          d.data().creationId === creation.id && d.data().creatorId === creation.sellerId
        );
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'watchingCreations', docToDelete.id));
        }
        setInternalIsWatching(false);
        onWatchChange?.(false);
      } else {
        // Add to watching
        await addDoc(watchingRef, {
          creationId: creation.id,
          creatorId: creation.sellerId,
          title: creation.title,
          photoUrl: photoUrl || null,
          price: creation.price || null,
          marketplaceCategory: creation.marketplaceCategory || null,
          addedAt: serverTimestamp()
        });
        setInternalIsWatching(true);
        onWatchChange?.(true);
      }
    } catch (err) {
      console.error('Error toggling watch status:', err);
    } finally {
      setWatchingLoading(false);
    }
  };

  // Toggle favorite seller
  const handleFavoriteToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    if (creation.sellerId === currentUserId) {
      return; // Can't favorite yourself
    }

    setFavoriteLoading(true);
    try {
      const favoritesRef = collection(db, 'users', currentUserId, 'favoriteSellers');
      
      if (isSellerFavorited) {
        // Remove from favorites
        const q = await getDocs(favoritesRef);
        const docToDelete = q.docs.find(d => d.data().sellerId === creation.sellerId);
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'favoriteSellers', docToDelete.id));
        }
        setIsSellerFavorited(false);
      } else {
        // Add to favorites
        await addDoc(favoritesRef, {
          sellerId: creation.sellerId,
          sellerUsername: sellerUsername || null,
          sellerAvatar: sellerAvatar || null,
          addedAt: serverTimestamp()
        });
        setIsSellerFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Calculate time since listed
  const getTimeAgo = (): string | null => {
    const listedDate = creation.marketplaceListedAt || creation.updatedAt || creation.createdAt;
    if (!listedDate) return null;

    const date = listedDate.toDate ? listedDate.toDate() : new Date(listedDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Listed today';
    if (diffDays === 1) return 'Listed yesterday';
    if (diffDays < 7) return `Listed ${diffDays} days ago`;
    if (diffDays < 30) return `Listed ${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `Listed ${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `Listed ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  };

  // Get category badge style
  const getCategoryBadge = () => {
    switch (creation.marketplaceCategory) {
      case 'pressons':
        return { bg: 'bg-purple-500', label: 'Press-Ons', icon: AppIcons.PressOns };
      case 'jewelry':
        return { bg: 'bg-amber-500', label: 'Jewelry', icon: AppIcons.Jewelry };
      case 'other':
        return { bg: 'bg-teal-500', label: 'Other', icon: Sparkles };
      default:
        return { bg: 'bg-gray-500', label: 'Creation', icon: AppIcons.Creation };
    }
  };

  // Get status badge styling
  const getStatusBadge = () => {
    const status = creation.marketplaceStatus || 'available';
    switch (status) {
      case 'available':
        return { bg: 'bg-green-500', text: 'Available' };
      case 'pending':
        return { bg: 'bg-yellow-500', text: 'Pending' };
      case 'sold':
        return { bg: 'bg-red-500', text: 'Sold' };
      default:
        return { bg: 'bg-green-500', text: 'Available' };
    }
  };

  const categoryBadge = getCategoryBadge();
  const statusBadge = getStatusBadge();
  const isSold = creation.marketplaceStatus === 'sold';

  // Prepare attached item for inquiry modal
  const getAttachedItem = (): AttachedItem => ({
    itemId: creation.id,
    ownerId: creation.sellerId,
    name: creation.title,
    brand: categoryBadge.label,
    photoUrl: photoUrl || null,
    status: creation.marketplaceStatus || 'available',
  });

  return (
    <>
      <div 
        className="card-light hover:shadow-2xl transition-all overflow-hidden group cursor-pointer flex flex-col"
        onClick={onClick}
      >
        {/* Image Section */}
        <div className="aspect-square relative overflow-hidden">
          {photoUrl ? (
            <img 
              src={photoUrl} 
              alt={creation.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-400 to-fuchsia-600 flex items-center justify-center">
              <AppIcons.Creation className="w-14 h-14 text-white" aria-label="Creation" />
            </div>
          )}

          {/* Category Badge - Top left */}
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 ${categoryBadge.bg} text-white rounded-full text-xs font-bold shadow flex items-center gap-1 font-body`}>
              {React.createElement(categoryBadge.icon, { className: 'w-3 h-3 text-current', 'aria-label': categoryBadge.label })}
              {categoryBadge.label}
            </span>
          </div>

          {/* Status Badge - Below category */}
          <div className="absolute top-10 left-2">
            <span className={`px-2 py-1 ${statusBadge.bg} text-white rounded-full text-xs font-bold shadow font-body`}>
              {statusBadge.text}
            </span>
          </div>

          {/* Watch Button (Star icon) - Bottom right */}
          {!isSold && (
            <button
              onClick={handleWatchToggle}
              disabled={watchingLoading}
              className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition-all ${
                isWatching
                  ? 'bg-amber-500 text-white'
                  : 'bg-white/90 text-light-secondary hover:bg-white hover:text-amber-500'
              }`}
              title={isWatching ? 'Stop watching' : 'Wishlist/Watchlist'}
            >
              {watchingLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Star className={`w-5 h-5 ${isWatching ? 'fill-current' : ''}`} />
              )}
            </button>
          )}

          {/* Sold Overlay */}
          {isSold && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-lg transform -rotate-12 shadow-lg">
                SOLD
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 flex flex-col flex-1">
          {/* Title */}
          <div className="mb-2">
            <h3 className="text-lg font-bold text-light-primary leading-tight font-body">
              {creation.title}
            </h3>
          </div>

          {/* Description */}
          {creation.description && (
            <p className="text-sm text-light-secondary mb-2 line-clamp-2 font-body">
              {creation.description}
            </p>
          )}

          {/* Price */}
          {creation.price && (
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold font-body ${
                isSold ? 'bg-gray-200 text-light-muted line-through' : 'bg-green-100 text-green-800'
              }`}>
                <Tag className="icon-sm" />
                ${creation.price.toFixed(2)}
              </span>
            </div>
          )}
          {creation.contactForPricing && !creation.price && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border-2 border-gray-300 text-light-secondary font-body">
                Contact for price
              </span>
            </div>
          )}

          {/* Press-on details badges */}
          {creation.marketplaceCategory === 'pressons' && creation.pressOnDetails && (
            <div className="mb-2 flex flex-wrap gap-1">
              {creation.pressOnDetails.customSizing && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                  Custom Sizing
                </span>
              )}
              {creation.pressOnDetails.sizingKitAvailable && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                  Sizing Kit
                </span>
              )}
            </div>
          )}

          {/* Time Listed */}
          {getTimeAgo() && !isSold && (
            <div className="mb-2 flex items-center gap-1 text-xs text-light-muted font-body">
              <Clock className="icon-xs" />
              {getTimeAgo()}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Divider */}
          <div className="border-t border border-gray-300 my-3"></div>

          {/* Seller Info with Favorite Heart */}
          <div className="flex items-center justify-between mb-3">
            {sellerUsername ? (
              <div className="flex items-center gap-2">
                <Link 
                  to={`/profile/${sellerUsername}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 text-sm text-light-secondary hover:text-purple-500 transition-default font-body"
                >
                  {sellerAvatar ? (
                    <img 
                      src={sellerAvatar} 
                      alt={sellerUsername}
                      className="w-6 h-6 rounded-full object-cover border border-gray-300"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold">
                      {sellerUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span>@{sellerUsername}</span>
                  <ExternalLink className="icon-xs" />
                </Link>
                
                {/* Favorite Seller Heart */}
                {currentUserId && creation.sellerId !== currentUserId && (
                  <button
                    onClick={handleFavoriteToggle}
                    disabled={favoriteLoading}
                    className={`p-1 rounded-full transition-all ${
                      isSellerFavorited
                        ? 'text-red-500'
                        : 'text-gray-300 hover:text-red-400'
                    }`}
                    title={isSellerFavorited ? 'Remove from favorites' : 'Add seller to favorites'}
                  >
                    <Heart className={`w-4 h-4 ${isSellerFavorited ? 'fill-current' : ''}`} />
                  </button>
                )}
              </div>
            ) : (
              <span className="flex items-center gap-2 text-sm text-light-muted font-body">
                <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse"></div>
                <span>Loading seller...</span>
              </span>
            )}
          </div>

          {/* Action Buttons */}
          {!isSold ? (
            <div className="flex gap-2">
              {/* Message Seller Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!currentUserId) {
                    setShowLoginPrompt(true);
                    return;
                  }
                  setShowInquiryModal(true);
                }}
                disabled={!currentUserId || creation.sellerId === currentUserId}
                className={`flex-1 py-2 px-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 font-body ${
                  !currentUserId || creation.sellerId === currentUserId
                    ? 'bg-gray-200 text-light-muted cursor-not-allowed'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                <MessageSquare className="icon-sm" />
                Message Seller
              </button>
            </div>
          ) : (
            <div className="text-center py-2 text-light-muted text-sm font-body">
              This creation has been sold
            </div>
          )}
          
          {!currentUserId && !isSold && (
            <p className="text-xs text-light-muted text-center mt-2 font-body">
              Sign in to message or watch
            </p>
          )}

          {/* Sizing guide link */}
          {creation.pressOnDetails?.guideUrl && (
            <a
              href={creation.pressOnDetails.guideUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-2 flex items-center justify-center gap-1 text-sm text-purple-600 hover:text-purple-800"
            >
              <ExternalLink className="icon-sm" />
              Sizing Guide
            </a>
          )}
        </div>
      </div>

      {/* Inquiry Modal */}
      {currentUserId && (
        <InquiryModal
          isOpen={showInquiryModal}
          onClose={() => setShowInquiryModal(false)}
          currentUserId={currentUserId}
          sellerId={creation.sellerId}
          sellerUsername={sellerUsername}
          items={[getAttachedItem()]}
        />
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 text-center">
            <div className="flex justify-center mb-4">
              <AppIcons.UserIcon className="w-16 h-16 text-purple-500" aria-label="Sign In" />
            </div>
            <h3 className="text-xl font-bold text-light-primary mb-2 font-heading">
              Sign In Required
            </h3>
            <p className="text-light-secondary mb-4 font-body">
              You need to be signed in to watch creations and message sellers.
            </p>
            <div className="flex gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLoginPrompt(false);
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-light-secondary rounded-lg hover:bg-gray-200 transition-default font-body"
              >
                Cancel
              </button>
              <Link
                to="/auth"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-default text-center font-body"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceCreationCard;
